import { useState, useEffect, useCallback } from "react";
import { Button, Space, Select, Input, DatePicker, Tag, Modal, Tooltip, message } from "antd";
import {
  PlusOutlined, ReloadOutlined, UploadOutlined, SendOutlined,
  PauseCircleOutlined, PlayCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import ExportButton from "../../components/common/ExportButton";
import BulkUploadModal from "../../components/common/BulkUploadModal";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import PayoutKPICards from "./PayoutKPICards";
import PayoutFormModal from "./PayoutFormModal";
import {
  listInProgressPayouts, listTransferredPayouts, listHoldPayouts, getPayoutKpis,
  createPayout, updatePayout, deletePayout, transferPayout, holdPayout,
  releasePayout, downloadPayoutTemplate, uploadPayouts, exportPayouts,
} from "../../services/payouts.service";

const LIST_FN = {
  inProgress: listInProgressPayouts,
  transferred: listTransferredPayouts,
  hold: listHoldPayouts,
};

const STATUS_COLORS = { inProgress: "blue", transferred: "green", hold: "orange" };

function formatOmr(value) {
  if (value === null || value === undefined || value === "") return "—";
  return Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

/**
 * Shared payout tab — In Progress / Transferred / Hold.
 *
 * Which actions appear depends on the status: only an in-progress payout can be
 * transferred or held, and only a held one can be released. The backend enforces
 * the same rules, so a stale page cannot skip a step.
 */
export default function PayoutTab({ status }) {
  const { canEdit, canDelete, canApprove } = usePermission("payouts");
  const { options: projectOptions } = useSettingsDropdown("projects");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState("");
  const [project, setProject] = useState();
  const [period, setPeriod] = useState(null);

  const [formModal, setFormModal] = useState({ open: false, record: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPeriod, setUploadPeriod] = useState(dayjs());
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState(null);

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      project: project || undefined,
      month: period ? period.month() + 1 : undefined,
      year: period ? period.year() : undefined,
      ...extra,
    }),
    [search, project, period]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        LIST_FN[status](params({ page, per_page: pageSize })),
        // KPIs cover the whole filtered set across every status, so the strip
        // reads the same on all three tabs.
        getPayoutKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load payouts");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, project, period, status]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (formModal.record) {
        await updatePayout(formModal.record._id, payload);
        message.success("Payout updated");
      } else {
        await createPayout(payload);
        message.success("Payout created");
      }
      setFormModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || err?.message || "Could not save payout");
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = (record) => {
    let reference = "";
    Modal.confirm({
      title: "Transfer Payout",
      content: (
        <div>
          <p>
            Transfer <b>{formatOmr(record.cashPayable)} OMR</b> to {record.partnerName}?
            The partner is notified and the payout becomes read-only.
          </p>
          <Input
            placeholder="Transfer reference (optional)"
            onChange={(e) => {
              reference = e.target.value;
            }}
          />
        </div>
      ),
      okText: "Transfer",
      onOk: async () => {
        try {
          await transferPayout(record._id, { transferReference: reference || undefined });
          message.success("Payout transferred");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not transfer payout");
        }
      },
    });
  };

  const handleHold = (record) => {
    let reason = "";
    Modal.confirm({
      title: "Hold Payout",
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Reason for holding this payout"
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: "Hold",
      okType: "danger",
      onOk: async () => {
        if (!reason.trim()) {
          message.error("Hold reason is required");
          return Promise.reject(new Error("reason required"));
        }
        try {
          await holdPayout(record._id, reason.trim());
          message.success("Payout put on hold");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not hold payout");
        }
      },
    });
  };

  const handleRelease = async (record) => {
    setActingId(record._id);
    try {
      await releasePayout(record._id);
      message.success("Hold released");
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not release hold");
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Payout",
      content: `Delete the ${record.monthLabel} payout for ${record.partnerName}?`,
      onConfirm: async () => {
        try {
          await deletePayout(record._id);
          message.success("Payout deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete payout");
        }
      },
    });
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "Month", dataIndex: "monthLabel" },
    { title: "Project", dataIndex: "projectName" },
    { title: "Orders", dataIndex: "completedOrders", align: "right" },
    { title: "Earnings", dataIndex: "orderEarnings", align: "right", render: formatOmr },
    { title: "Incentives", dataIndex: "incentivePay", align: "right", render: formatOmr },
    { title: "Tips", dataIndex: "tips", align: "right", render: formatOmr },
    { title: "Deductions", dataIndex: "deductions", align: "right", render: formatOmr },
    {
      title: "COD Deductions",
      key: "cod",
      align: "right",
      render: (_, r) =>
        formatOmr(
          Number(r.currentCodDeductions || 0) +
            Number(r.previousCodDeductions || 0) +
            Number(r.advanceCodDeductions || 0)
        ),
    },
    {
      title: "Cash Payable (OMR)",
      dataIndex: "cashPayable",
      align: "right",
      render: (value) => <b>{formatOmr(value)}</b>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLORS[value] || "default"}>{r.statusLabel}</Tag>
          {value === "hold" && r.holdReason && (
            <Tooltip title={r.holdReason}>
              <Tag color="red">reason</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    ...(canApprove
      ? [
          {
            title: "Action",
            key: "action",
            width: 190,
            render: (_, record) => (
              <Space size="small">
                {record.status === "inProgress" && (
                  <>
                    <Button
                      size="small"
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={() => handleTransfer(record)}
                    >
                      Transfer
                    </Button>
                    <Button
                      size="small"
                      icon={<PauseCircleOutlined />}
                      onClick={() => handleHold(record)}
                    >
                      Hold
                    </Button>
                  </>
                )}
                {record.status === "hold" && (
                  <Button
                    size="small"
                    icon={<PlayCircleOutlined />}
                    loading={actingId === record._id}
                    onClick={() => handleRelease(record)}
                  >
                    Release
                  </Button>
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <PayoutKPICards kpis={kpis} />

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search partner ID or name"
          allowClear
          style={{ width: 260 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Project"
          allowClear
          style={{ width: 170 }}
          options={projectOptions}
          value={project}
          onChange={setProject}
        />
        <DatePicker
          picker="month"
          format="MM/YYYY"
          onChange={setPeriod}
          placeholder="Payout month"
        />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Payouts"
          onExport={() => exportPayouts(params({ status }))}
        />
        {canEdit && (
          <>
            <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setFormModal({ open: true, record: null })}
            >
              Add Payout
            </Button>
          </>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? (record) => setFormModal({ open: true, record }) : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
          // A transferred payout is settled money — the backend refuses edits,
          // so the button is disabled rather than failing on click.
          isEditDisabled: (record) => record.status === "transferred",
          getEditDisabledReason: () => "A transferred payout cannot be edited",
        }}
      />

      <PayoutFormModal
        open={formModal.open}
        record={formModal.record}
        saving={saving}
        onCancel={() => setFormModal({ open: false, record: null })}
        onSubmit={handleSave}
      />

      <BulkUploadModal
        open={uploadOpen}
        title="Upload Payouts"
        templateName="EZYGO_Payouts_Upload_Template"
        hint="CSV using the downloaded template's columns — the same fields as the individual payout form. Month and year come from the selector below, not the file."
        extra={
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#6b7280" }}>
              Payout month for every row in this file
            </div>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              value={uploadPeriod}
              onChange={(value) => setUploadPeriod(value || dayjs())}
              allowClear={false}
              style={{ width: "100%" }}
            />
          </div>
        }
        onDownloadTemplate={downloadPayoutTemplate}
        onUpload={(file) =>
          uploadPayouts(file, {
            month: uploadPeriod.month() + 1,
            year: uploadPeriod.year(),
          })
        }
        onClose={() => setUploadOpen(false)}
        onSuccess={fetchAll}
      />
    </div>
  );
}
